import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import type { MusicTrack, Playlist } from '@/lib/musicTypes';

/**
 * Hook to fetch events (tracks/playlists) that a user has liked
 */
export function useLikedEvents(userPubkey?: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['liked-events', userPubkey],
    queryFn: async (c) => {
      if (!userPubkey) {
        return { tracks: [], playlists: [] };
      }

      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(5000)]);

      // Get all like reactions from this user
      const userLikes = await nostr.query(
        [
          {
            kinds: [7],
            authors: [userPubkey],
            limit: 500,
          },
        ],
        { signal }
      );

      // Filter for heart reactions only
      const heartLikes = userLikes.filter(
        (r) => r.content === '❤️' || r.content === '❤'
      );

      // Extract event IDs and 'a' tags from the likes
      const likedEventIds = heartLikes
        .map((like) => {
          const eTag = like.tags.find((t) => t[0] === 'e');
          return eTag?.[1];
        })
        .filter((id): id is string => !!id);

      const likedATags = heartLikes
        .map((like) => {
          const aTag = like.tags.find((t) => t[0] === 'a');
          return aTag?.[1];
        })
        .filter((a): a is string => !!a);

      // Parse 'a' tags to get kind, author, and identifier
      const trackCoordinates: Array<{ kind: number; author: string; identifier: string }> = [];
      const playlistCoordinates: Array<{ kind: number; author: string; identifier: string }> = [];

      likedATags.forEach((aTag) => {
        const [kindStr, author, identifier] = aTag.split(':');
        const kind = parseInt(kindStr, 10);

        if (kind === 36787) {
          trackCoordinates.push({ kind, author, identifier });
        } else if (kind === 34139) {
          playlistCoordinates.push({ kind, author, identifier });
        }
      });

      // Fetch the actual track events
      const trackEvents: NostrEvent[] = [];
      if (trackCoordinates.length > 0) {
        const trackFilters = trackCoordinates.map((coord) => ({
          kinds: [coord.kind],
          authors: [coord.author],
          '#d': [coord.identifier],
        }));

        const tracks = await nostr.query(trackFilters, { signal });
        trackEvents.push(...tracks);
      }

      // Fetch the actual playlist events
      const playlistEvents: NostrEvent[] = [];
      if (playlistCoordinates.length > 0) {
        const playlistFilters = playlistCoordinates.map((coord) => ({
          kinds: [coord.kind],
          authors: [coord.author],
          '#d': [coord.identifier],
        }));

        const playlists = await nostr.query(playlistFilters, { signal });
        playlistEvents.push(...playlists);
      }

      // Transform events into MusicTrack and Playlist objects
      const tracks: MusicTrack[] = trackEvents.map((event) => {
        const getTag = (name: string) => event.tags.find((t) => t[0] === name)?.[1] || '';
        const getAllTags = (name: string) =>
          event.tags.filter((t) => t[0] === name).map((t) => t[1]);

        const gradientTag = event.tags.find(
          (t) => t[0] === 'gradient' && t[1] === 'colors' && t[2]
        );
        const gradient = gradientTag?.[2]?.split(',').filter((c) => c.trim());

        return {
          event,
          pubkey: event.pubkey,
          id: getTag('d'),
          title: getTag('title'),
          url: getTag('url'),
          imageUrl: getTag('image'),
          gradient,
          artist: getTag('artist'),
          album: getTag('album'),
          trackNumber: getTag('track_number'),
          released: getTag('released'),
          genres: getAllTags('t').filter((t) => t !== 'music'),
          language: getTag('language'),
          explicit: getTag('explicit') === 'true',
          duration: parseInt(getTag('duration')) || undefined,
          format: getTag('format'),
          bitrate: getTag('bitrate'),
          sampleRate: getTag('sample_rate'),
        };
      });

      const playlists: Playlist[] = playlistEvents.map((event) => {
        const getTag = (name: string) => event.tags.find((t) => t[0] === name)?.[1] || '';
        const getAllTags = (name: string) =>
          event.tags.filter((t) => t[0] === name).map((t) => t[1]);

        const gradientTag = event.tags.find(
          (t) => t[0] === 'gradient' && t[1] === 'colors' && t[2]
        );
        const gradient = gradientTag?.[2]?.split(',').filter((c) => c.trim());

        const trackRefs = event.tags
          .filter((t) => t[0] === 'a' && t[1]?.startsWith('36787:'))
          .map((t) => {
            const [kind, pubkey, identifier] = t[1].split(':');
            return {
              kind: parseInt(kind),
              pubkey,
              identifier,
            };
          });

        return {
          event,
          pubkey: event.pubkey,
          id: getTag('d'),
          title: getTag('title'),
          description: getTag('description') || event.content,
          imageUrl: getTag('image'),
          gradient,
          trackRefs,
          isPublic: getTag('public') === 'true',
          isCollaborative: getTag('collaborative') === 'true',
        };
      });

      // Sort by most recently liked (based on reaction created_at)
      const eventIdToLikeTime = new Map<string, number>();
      heartLikes.forEach((like) => {
        const eTag = like.tags.find((t) => t[0] === 'e')?.[1];
        const aTag = like.tags.find((t) => t[0] === 'a')?.[1];
        const identifier = eTag || aTag;
        if (identifier) {
          const existingTime = eventIdToLikeTime.get(identifier);
          if (!existingTime || like.created_at > existingTime) {
            eventIdToLikeTime.set(identifier, like.created_at);
          }
        }
      });

      tracks.sort((a, b) => {
        const aTime = eventIdToLikeTime.get(a.event.id) || 0;
        const bTime = eventIdToLikeTime.get(b.event.id) || 0;
        return bTime - aTime;
      });

      playlists.sort((a, b) => {
        const aTime = eventIdToLikeTime.get(a.event.id) || 0;
        const bTime = eventIdToLikeTime.get(b.event.id) || 0;
        return bTime - aTime;
      });

      return { tracks, playlists };
    },
    enabled: !!userPubkey,
    staleTime: 0, // Always consider data stale
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
}
